import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import SharedHeader from '../../../components/shared/SharedHeader';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import DistributionForm from './DistributionForm';
import { apiClient } from '../../../api/apiClient';
import { 
  GET_DISTRIBUTOR_SONGS_URL,
  GET_DISTRIBUTOR_CONFLICTS_URL,
  GET_DISTRIBUTOR_LABELS_URL,
  UPDATE_DISTRIBUTOR_URL 
} from '../../../api/distributor';
import { toast } from '../../../components/shared/toast';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';
import Pagination from '../../../components/shared/Pagination';
import { isActiveDistributorSong, getDistributorSongId } from './distributorTabUtils';
import './DistributorDetails.css';

const TABS = [
  { id: 'basic', labelKey: 'distribution.details.tabBasicDetails', labelDefault: 'Basic Details' },
  { id: 'songs', labelKey: 'distribution.details.tabSongs', labelDefault: 'Songs' },
  { id: 'labels', labelKey: 'distribution.details.tabLabels', labelDefault: 'Labels' },
  { id: 'conflicts', labelKey: 'distribution.details.tabConflicts', labelDefault: 'Conflicts' },
  { id: 'cases_notes', labelKey: 'distribution.details.tabCasesNotes', labelDefault: 'Cases and Notes' }
];

const MOCK_LABELS = [
  { id: 1, name: 'Ransilu Music Group', songCount: 5600 },
  { id: 2, name: 'Lanka Records', songCount: 3200 },
  { id: 3, name: 'Sinha Beats', songCount: 1400 }
];

function DistributorDetails({ distItem, onBack, onInactivate, onActivate, onDelete, onUpdateSuccess, onSelectSong, onSelectCase, t }) {
  const [activeTab, setActiveTab] = useState('basic');
  const [isEditing, setIsEditing] = useState(false);

  // Songs tab lazy loading state
  const [songs, setSongs] = useState([]);
  const [songsTotalCount, setSongsTotalCount] = useState(0);
  const [songsCurrentPage, setSongsCurrentPage] = useState(1);
  const [hasMoreSongs, setHasMoreSongs] = useState(true);
  const [isSongsLoadingMore, setIsSongsLoadingMore] = useState(false);
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [isSongsLoading, setIsSongsLoading] = useState(false);
  const [isExportingSongs, setIsExportingSongs] = useState(false);

  // Songs tab filter state
  const [filterLabelId, setFilterLabelId] = useState(null);
  const [filterLabels, setFilterLabels] = useState([]);
  const [isFilterLabelsLoading, setIsFilterLabelsLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleOutsideClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isFilterOpen]);

  // Conflicts tab state
  const [conflictsList, setConflictsList] = useState([]);
  const [isConflictsLoading, setIsConflictsLoading] = useState(false);
  const [conflictSearchQuery, setConflictSearchQuery] = useState('');

  // Labels tab state
  const [labelsList, setLabelsList] = useState([]);
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);

  // Cases & Notes tab state
  const [casesList, setCasesList] = useState([]);
  const [notesList, setNotesList] = useState([]);
  const [isCasesNotesLoading, setIsCasesNotesLoading] = useState(false);
  const [casesPage, setCasesPage] = useState(1);
  const [notesPage, setNotesPage] = useState(1);

  const isActive = distItem.status === 'Active' || distItem.status === 1;

  // Lazy load songs when Songs tab is active
  const fetchDistributorSongs = useCallback(async (pageNum = 1, append = false, labelId = filterLabelId) => {
    if (activeTab !== 'songs') return;
    try {
      if (append) setIsSongsLoadingMore(true);
      else setIsSongsLoading(true);

      let url = `${GET_DISTRIBUTOR_SONGS_URL(distItem.id)}?page=${pageNum}&limit=20`;
      if (labelId) url += `&recordLabelId=${labelId}`;
      const response = await apiClient(url);
      if (response && response.songs) {
        setSongs(prev => append ? [...prev, ...response.songs] : (response.songs || []));
        setSongsTotalCount(response.totalCount || 0);
        setHasMoreSongs(response.songs.length === 20);
      }
    } catch (err) {
      console.error('Failed to fetch distributor songs:', err);
      if (!append) {
        setSongs([]);
        setSongsTotalCount(0);
      }
    } finally {
      setIsSongsLoading(false);
      setIsSongsLoadingMore(false);
    }
  }, [distItem.id, activeTab, filterLabelId]);

  useEffect(() => {
    if (activeTab === 'songs') {
      setSongsCurrentPage(1);
      setFilterLabelId(null);
      setIsFilterOpen(false);
      fetchDistributorSongs(1, false, null);
      // Fetch labels for filter dropdown
      (async () => {
        try {
          setIsFilterLabelsLoading(true);
          const resp = await apiClient(GET_DISTRIBUTOR_LABELS_URL(distItem.id));
          if (resp && resp.labels) setFilterLabels(resp.labels);
          else setFilterLabels([]);
        } catch (e) {
          setFilterLabels([]);
        } finally {
          setIsFilterLabelsLoading(false);
        }
      })();
    }
  }, [activeTab, distItem.id]);

  const handleSongsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMoreSongs && !isSongsLoadingMore && !isSongsLoading) {
      const nextPage = songsCurrentPage + 1;
      setSongsCurrentPage(nextPage);
      fetchDistributorSongs(nextPage, true);
    }
  };

  // Lazy load active conflicts when Conflicts tab is active
  const fetchDistributorConflicts = useCallback(async () => {
    if (activeTab !== 'conflicts') return;
    try {
      setIsConflictsLoading(true);
      const queryParams = new URLSearchParams({
        search: conflictSearchQuery,
      }).toString();
      const response = await apiClient(`${GET_DISTRIBUTOR_CONFLICTS_URL(distItem.id)}?${queryParams}`);
      if (response && response.conflicts) {
        setConflictsList(response.conflicts);
      } else {
        setConflictsList([]);
      }
    } catch (err) {
      console.error('Failed to fetch distributor conflicts:', err);
      setConflictsList([]);
    } finally {
      setIsConflictsLoading(false);
    }
  }, [distItem.id, activeTab, conflictSearchQuery]);

  useEffect(() => {
    fetchDistributorConflicts();
  }, [fetchDistributorConflicts]);

  // Lazy load unique record labels when Labels tab is active
  const fetchDistributorLabels = useCallback(async () => {
    if (activeTab !== 'labels') return;
    try {
      setIsLabelsLoading(true);
      const response = await apiClient(GET_DISTRIBUTOR_LABELS_URL(distItem.id));
      if (response && response.labels) {
        setLabelsList(response.labels);
      }
    } catch (err) {
      console.error('Failed to fetch distributor labels:', err);
      setLabelsList([]);
    } finally {
      setIsLabelsLoading(false);
    }
  }, [distItem.id, activeTab]);

  useEffect(() => {
    fetchDistributorLabels();
  }, [distItem.id, activeTab]);

  // Client-side filter fallback for search within tab
  const filteredSongs = useMemo(() => {
    if (!songSearchQuery.trim()) return songs;
    const q = songSearchQuery.toLowerCase();
    return songs.filter(s => 
      s.name.toLowerCase().includes(q) || 
      s.artist.toLowerCase().includes(q)
    );
  }, [songs, songSearchQuery]);

  const fetchDistributorCasesNotes = useCallback(async () => {
    if (activeTab !== 'cases_notes') return;

    try {
      setIsCasesNotesLoading(true);
      setCasesPage(1);
      setNotesPage(1);

      const songsRes = await apiClient(`${GET_DISTRIBUTOR_SONGS_URL(distItem.id)}?page=1&limit=500`);
      const rawSongs = Array.isArray(songsRes?.songs)
        ? songsRes.songs
        : Array.isArray(songsRes?.data)
          ? songsRes.data
          : [];

      const activeSongsOnly = rawSongs.filter(isActiveDistributorSong);
      const songIds = activeSongsOnly.map(getDistributorSongId).filter(Boolean);

      if (!songIds.length) {
        setCasesList([]);
        setNotesList([]);
        return;
      }

      const queryParams = new URLSearchParams({ songIds: songIds.join(','), limit: '100', status: 'all' });
      const response = await apiClient(`/notes-and-cases?${queryParams.toString()}`);
      const items = Array.isArray(response?.items) ? response.items : [];

      setCasesList(items.filter((item) => item?.type === 'case'));
      setNotesList(items.filter((item) => item?.type === 'note'));
    } catch (err) {
      console.error('Failed to fetch distributor cases and notes:', err);
      setCasesList([]);
      setNotesList([]);
      toast.error('Failed to load notes and cases for this distributor.');
    } finally {
      setIsCasesNotesLoading(false);
    }
  }, [activeTab, distItem.id]);

  useEffect(() => {
    fetchDistributorCasesNotes();
  }, [activeTab, distItem.id]);

  const pagedCases = useMemo(() => casesList.slice((casesPage - 1) * 20, casesPage * 20), [casesList, casesPage]);
  const pagedNotes = useMemo(() => notesList.slice((notesPage - 1) * 20, notesPage * 20), [notesList, notesPage]);

  const getConflictSongId = (song = {}) => {
    const nestedSong = song.song || song.songData || song.song_data;
    return (
      song.songId ||
      song.song_id ||
      nestedSong?.id ||
      nestedSong?.songId ||
      nestedSong?.song_id ||
      song.song?.id ||
      song.id ||
      null
    );
  };

  const getConflictSongSelection = (song) => {
    const songId = getConflictSongId(song);
    return {
      ...song,
      id: songId || song.id,
      songId: songId || song.songId || song.song_id,
      conflictId: song.conflictId || song.id,
    };
  };

  const handleConflictSongClick = async (song) => {
    if (!onSelectSong) return;

    const selection = getConflictSongSelection(song);

    try {
      if (selection.id) {
        const canonicalSong = await apiClient(`/songs/${selection.id}`);
        if (canonicalSong) {
          onSelectSong({
            ...canonicalSong,
            conflictId: selection.conflictId,
            songId: selection.id,
          });
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to load canonical song for conflict row:', err);
    }

    onSelectSong(selection);
  };

  const handleExportSongs = async () => {
    try {
      setIsExportingSongs(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const response = await apiClient(`${GET_DISTRIBUTOR_SONGS_URL(distItem.id)}?export=true`);
      if (response && response.songs) {
        const headers = ['Song Name', 'Sinhala Name', 'Status', 'Artist', 'Lyrics', 'Music', 'Album', 'Release Date', 'ISRC Code', 'Version Type', 'Ownership', 'Conflicts'];
        const rows = response.songs.map(s => [
          s.name,
          s.nameSinhala || '',
          s.status,
          s.artist,
          s.lyrics,
          s.music,
          s.album,
          s.releaseDate,
          s.isrcCode,
          s.versionType,
          `${s.ownership}%`,
          s.conflicts || s.conflict || 'No'
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const formattedCompany = (distItem.company || 'distributor').replace(/\s+/g, '_');
        link.setAttribute('download', `distributor_songs_${formattedCompany}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Distributor songs exported successfully!');
      } else {
        toast.error('Failed to export songs.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export songs list.');
    } finally {
      setIsExportingSongs(false);
    }
  };

  const handleSaveEdit = async (updatedData) => {
    try {
      const response = await apiClient(UPDATE_DISTRIBUTOR_URL(distItem.id), {
        method: 'PUT',
        body: updatedData
      });
      if (response) {
        toast.success('Distributor updated successfully!');
        setIsEditing(false);
        if (onUpdateSuccess) onUpdateSuccess();
      }
    } catch (err) {
      console.error('Failed to update distributor:', err);
      toast.error('Failed to update distributor: ' + (err.message || ''));
    }
  };

  if (isEditing) {
    return (
      <DistributionForm 
        isModal={false}
        distributor={distItem}
        onCancel={() => setIsEditing(false)}
        onSave={handleSaveEdit}
      />
    );
  }

  const totalPages = Math.ceil(songsTotalCount / 20) || 1;

  return (
    <div className="distributor-details-wrapper">
      {/* Navigation path breadcrumbs */}
      <div className="breadcrumb-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
        <button 
          type="button" 
          className="breadcrumb-link" 
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted, #64748b)', fontWeight: '500' }}
        >
          {t('distribution.details.breadcrumb') || 'Distributors'}
        </button>
        <span className="breadcrumb-separator" style={{ color: '#cbd5e1' }}>&gt;</span>
        <span className="breadcrumb-current" style={{ color: 'var(--text-main, #0f172a)', fontWeight: '600' }}>{distItem.email}</span>
      </div>

      {/* Top Banner Card displaying company name and status */}
      <div className="distributor-details-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="distributor-banner-name">{distItem.company} ({distItem.email})</h1>
        <span className={`status-pill ${isActive ? 'active' : 'inactive'}`}>
          <span className="status-dot"></span>
          {distItem.status}
        </span>
      </div>

      {/* Tab Selectors Row */}
      <div className="details-tab-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`details-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey) || tab.labelDefault}
          </button>
        ))}
      </div>

      {/* Basic Details tab content panel */}
      {activeTab === 'basic' && (
        <div className="tab-panel-container" style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color, #e2e8f0)', padding: '24px', boxShadow: 'var(--card-shadow, 0 1px 3px rgba(0,0,0,0.05))' }}>
          <div className="info-rows-list">
            {/* Email Row */}
            <div className="info-row">
              <div className="info-icon-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <span className="info-label">{t('distribution.details.emailLabel') || 'Distribution account - email'}</span>
              <span className="info-separator">:</span>
              <span className="info-value">{distItem.email}</span>
            </div>

            {/* Company Row */}
            <div className="info-row">
              <div className="info-icon-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18" />
                  <path d="M5 21V7l8-4v18" />
                  <path d="M19 21V11l-6-3" />
                  <path d="M9 9h.01" />
                  <path d="M9 12h.01" />
                  <path d="M9 15h.01" />
                  <path d="M9 18h.01" />
                </svg>
              </div>
              <span className="info-label">{t('distribution.details.companyLabel') || 'Distribution company'}</span>
              <span className="info-separator">:</span>
              <span className="info-value">{distItem.company}</span>
            </div>

            {/* Outgoing Percentage Row */}
            <div className="info-row">
              <div className="info-icon-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="5" x2="5" y2="19" />
                  <circle cx="6.5" cy="6.5" r="2.5" />
                  <circle cx="17.5" cy="17.5" r="2.5" />
                </svg>
              </div>
              <span className="info-label">{t('distribution.details.percentageLabel') || 'Out Going percentage'}</span>
              <span className="info-separator">:</span>
              <span className="info-value">{distItem.percentage}%</span>
            </div>
          </div>

          {/* Action buttons footer aligned right */}
          <div className="basic-details-actions-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            {isActive ? (
              <>
                <button 
                  type="button" 
                  onClick={() => setIsEditing(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #cbd5e1)',
                    background: '#ffffff',
                    color: '#0f172a',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  <span>{t('distribution.details.btnEdit') || 'Edit'}</span>
                </button>

                <button 
                  type="button" 
                  onClick={() => onInactivate && onInactivate(distItem)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#fef3c7',
                    color: '#d97706',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  <span>{t('distribution.details.btnInactive') || 'Inactive'}</span>
                </button>
              </>
            ) : (
              <>
                <button 
                  type="button" 
                  onClick={() => onActivate && onActivate(distItem)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#dcfce7',
                    color: '#16a34a',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>{t('distribution.details.btnReactivate') || 'Reactivate'}</span>
                </button>

                <button 
                  type="button" 
                  onClick={() => onDelete && onDelete(distItem)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#fee2e2',
                    color: '#ef4444',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span>{t('distribution.details.btnDelete') || 'Delete'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Songs tab content panel */}
      {activeTab === 'songs' && (
        <div className="songs-list-tab-content">
          <div ref={filterRef} style={{ position: 'relative' }}>
            <SharedHeader
              title=""
              searchQuery={songSearchQuery}
              onSearchChange={setSongSearchQuery}
              searchPlaceholder={t('songs.searchPlaceholder') || 'Search songs...'}
              resultCount={songsTotalCount}
              resultLabel="Songs"
              showSortFilter={true}
              onSortClick={null}
              onFilterClick={() => setIsFilterOpen(prev => !prev)}
              filterLabel={filterLabelId ? (filterLabels.find(l => l.id === filterLabelId)?.name || 'Filter') : 'Filter'}
              isFilterApplied={!!filterLabelId}
              filterActive={isFilterOpen}
              onExportClick={handleExportSongs}
              onAddNewClick={null}
              addNewText={null}
            />

            {/* Filter Dropdown Panel */}
            {isFilterOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 200,
                  background: 'var(--card-bg, #ffffff)',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: '8px 0',
                  minWidth: '220px',
                  maxHeight: '320px',
                  overflowY: 'auto'
                }}
              >
                <div style={{ padding: '8px 14px 4px', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Filter by Record Label
                </div>

                {/* All Labels option */}
                <button
                  type="button"
                  onClick={() => {
                    setFilterLabelId(null);
                    setIsFilterOpen(false);
                    setSongsCurrentPage(1);
                    fetchDistributorSongs(1, false, null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '9px 14px',
                    border: 'none',
                    background: filterLabelId === null ? 'var(--primary-soft, #eff6ff)' : 'transparent',
                    color: filterLabelId === null ? 'var(--primary, #3b82f6)' : 'var(--text-main, #0f172a)',
                    fontWeight: filterLabelId === null ? '700' : '500',
                    fontSize: '13px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s'
                  }}
                >
                  <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e2e8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: '#64748b', flexShrink: 0 }}>All</span>
                  All Labels
                </button>

                {isFilterLabelsLoading ? (
                  <div style={{ padding: '10px 14px', fontSize: '13px', color: 'var(--text-muted, #64748b)' }}>Loading labels...</div>
                ) : filterLabels.length === 0 ? (
                  <div style={{ padding: '10px 14px', fontSize: '13px', color: 'var(--text-muted, #64748b)' }}>No record labels found.</div>
                ) : (
                  filterLabels.map(lbl => {
                    const isSelected = filterLabelId === lbl.id;
                    const labelName = lbl.display_name || lbl.name || 'Label';
                    const initial = labelName.charAt(0).toUpperCase();
                    const img = lbl.image_url || lbl.imageUrl;
                    return (
                      <button
                        key={lbl.id}
                        type="button"
                        onClick={() => {
                          setFilterLabelId(lbl.id);
                          setIsFilterOpen(false);
                          setSongsCurrentPage(1);
                          fetchDistributorSongs(1, false, lbl.id);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '9px 14px',
                          border: 'none',
                          background: isSelected ? 'var(--primary-soft, #eff6ff)' : 'transparent',
                          color: isSelected ? 'var(--primary, #3b82f6)' : 'var(--text-main, #0f172a)',
                          fontWeight: isSelected ? '700' : '500',
                          fontSize: '13px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'background 0.15s'
                        }}
                      >
                        {img ? (
                          <img src={img} alt={labelName} style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: isSelected ? 'var(--primary, #3b82f6)' : '#e2e8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: isSelected ? '#fff' : '#64748b', flexShrink: 0 }}>{initial}</span>
                        )}
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelName}</span>
                        {lbl.songCount > 0 && (
                          <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', flexShrink: 0 }}>{lbl.songCount}</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div 
            className="song-table-container" 
            onScroll={handleSongsScroll}
            style={{ marginTop: '16px', maxHeight: '550px', overflowY: 'auto' }}
          >
            <div className="song-table-header song-table-grid recode-songs-table-grid">
              <div>{t('distribution.details.colSongName') || 'Song Name'}</div>
              <div>{t('distribution.details.colStatus') || 'Status'}</div>
              <div>{t('distribution.details.colArtist') || 'Artist'}</div>
              <div>{t('distribution.details.colLyricsMusic') || 'Lyrics & Music'}</div>
              <div>{t('distribution.details.colLabels') || 'Labels'}</div>
              <div>{t('distribution.details.colOwnership') || 'Ownership'}</div>
              <div>{t('distribution.details.colNotesCases') || 'Notes / Cases'}</div>
              <div>{t('distribution.details.colConflicts') || 'Conflicts'}</div>
            </div>

            <div className="song-table-body">
              {isSongsLoading ? (
                <Loader label="Fetching songs, please wait." />
              ) : filteredSongs.length > 0 ? (
                filteredSongs.map((song) => (
                  <div 
                    key={song.id} 
                    className="song-row song-table-grid recode-songs-table-grid"
                    onClick={() => onSelectSong && onSelectSong(song)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <div className="bold-text">{song.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.isrcCode || 'Song Name'}</div>
                    </div>
                    <div>
                      <span className={`status-pill ${song.status === 'Active' ? 'active' : 'inactive'}`}>
                        <span className="status-dot"></span>
                        {song.status}
                      </span>
                    </div>
                    <div>
                      <div className="bold-text">{song.artist}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.versionType}</div>
                    </div>
                    <div>
                      <div className="bold-text">{song.lyrics}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.music}</div>
                    </div>
                    <div>
                      <LabelAvatarStack labels={song.labels || song.recordLabels} />
                    </div>
                    <div>{song.ownership !== undefined ? song.ownership : (song.ownershipPercentage !== undefined ? song.ownershipPercentage : 0)}%</div>
                    <div>{song.notes || 'No Cases Or Notes'}</div>
                    <div>
                      <span className={`conflicts-badge ${song.conflictCount > 0 || (song.conflict && song.conflict !== 'No' && song.conflict !== '0') ? 'conflict' : 'no-conflict'}`}>
                        {song.conflicts || song.conflict || 'No'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState title="No Songs Found" subtitle="This distributor has no songs associated yet." />
              )}
            </div>
          </div>

          {/* Pagination Row */}
          {songsTotalCount > 0 && (
            <div className="ringtone-pagination-row">
              <span className="pagination-range-text">
                {(songsCurrentPage - 1) * 20 + 1}-{Math.min(songsCurrentPage * 20, songsTotalCount)} of {songsTotalCount}
              </span>
              <div className="pagination-controls-wrapper">
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={songsCurrentPage === 1}
                  onClick={() => setSongsCurrentPage(1)}
                >
                  &lt;&lt;
                </button>
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={songsCurrentPage === 1}
                  onClick={() => setSongsCurrentPage(songsCurrentPage - 1)}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button 
                    key={num}
                    type="button" 
                    className={`pager-btn ${songsCurrentPage === num ? 'active' : 'outlined'}`}
                    onClick={() => setSongsCurrentPage(num)}
                  >
                    {num}
                  </button>
                ))}
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={songsCurrentPage === totalPages}
                  onClick={() => setSongsCurrentPage(songsCurrentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Labels tab content panel */}
      {activeTab === 'labels' && (
        <div className="labels-tab-panel" style={{ marginTop: '16px' }}>
          {isLabelsLoading ? (
            <Loader label="Fetching record labels, please wait." />
          ) : labelsList.length === 0 ? (
            <EmptyState title="No Record Labels Found" subtitle="This distributor has no associated record labels yet." />
          ) : (
            <div className="labels-grid">
              {labelsList.map((lbl, idx) => {
                const colorClasses = ['label-purple', 'label-blue', 'label-green', 'label-orange'];
                const colorClass = colorClasses[idx % colorClasses.length];
                const img = lbl.image_url || lbl.imageUrl;
                const labelName = lbl.display_name || lbl.name || 'Record Label';
                const initial = labelName.charAt(0).toUpperCase();
                const songCountText = lbl.songCount ? `${lbl.songCount} ${lbl.songCount === 1 ? 'Song' : 'Songs'}` : 'Record Label';

                return (
                  <div key={lbl.id || idx} className="label-badge-card">
                    <div className="label-card-thumb">
                      {img ? (
                        <img 
                          src={img} 
                          alt={labelName} 
                          className="label-card-img-thumb"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.parentElement.querySelector('.label-icon-wrapper');
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`label-icon-wrapper ${colorClass}`}
                        style={{ display: img ? 'none' : 'flex' }}
                      >
                        <span className="label-initial">{initial}</span>
                      </div>
                    </div>
                    <div className="label-card-info">
                      <span className="label-card-name" title={labelName}>{labelName}</span>
                      <span className="label-card-subtext">{songCountText}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Conflicts tab content panel */}
      {activeTab === 'conflicts' && (
        <div className="conflicts-tab-panel">
          <SharedHeader
            title=""
            searchQuery={conflictSearchQuery}
            onSearchChange={setConflictSearchQuery}
            searchPlaceholder={t('distribution.details.searchConflictsPlaceholder') || 'Search conflicts...'}
            resultCount={conflictsList.length}
            resultLabel="Conflicts"
            onSortClick={null}
            onFilterClick={null}
            onExportClick={null}
            onAddNewClick={null}
            addNewText={null}
          />

          <div className="song-table-container" style={{ marginTop: '16px' }}>
            <div className="song-table-header song-table-grid distributor-conflicts-table-grid">
              <div>{t('distribution.details.colSongName') || 'Song Name'}</div>
              <div>{t('distribution.details.colArtist') || 'Artist'}</div>
              <div>{t('distribution.details.colLyricsMusic') || 'Lyrics & Music'}</div>
              <div>{t('distribution.details.colLabels') || 'Labels'}</div>
              <div>{t('distribution.details.colOwnership') || 'Ownership'}</div>
              <div>{t('distribution.details.colConflictOwner') || 'Conflict Owner'}</div>
              <div>{t('distribution.details.colCopyrightConflict') || 'Copyright in conflict'}</div>
              <div>{t('distribution.details.colNotesCases') || 'Notes / Cases'}</div>
            </div>

            <div className="song-table-body">
              {isConflictsLoading ? (
                <Loader label="Fetching conflicts, please wait." />
              ) : conflictsList.length > 0 ? (
                conflictsList.map((song) => (
                  <div 
                    key={song.id} 
                    className="song-row song-table-grid distributor-conflicts-table-grid"
                      onClick={() => handleConflictSongClick(song)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <div className="bold-text">{song.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Song Name</div>
                    </div>
                    <div>
                      <div className="bold-text">{song.artist}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Singer</div>
                    </div>
                    <div>
                      <div className="bold-text">{song.lyrics}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{song.music}</div>
                    </div>
                    <div>
                      <LabelAvatarStack labels={song.labels || song.recordLabels} />
                    </div>
                    <div>{song.ownership}</div>
                    <div className="bold-text">{song.conflictOwner}</div>
                    <div className="bold-text">{song.copyrightConflict}</div>
                    <div>{song.notes}</div>
                  </div>
                ))
              ) : (
                <EmptyState title="No Conflicts Found" subtitle="This distributor has no active conflicts." />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cases and Notes tab content panel */}
      {activeTab === 'cases_notes' && (
        <div className="cases-notes-tab-panel" style={{ textAlign: 'left' }}>
          <div className="tab-panel-header-row">
            <span className="panel-header-title">Related Cases and Notes</span>
          </div>

          {isCasesNotesLoading ? (
            <LoadingOverlay isLoading message="Loading Notes & Cases..." />
          ) : (
            <>
              <div className="cases-section">
                <div className="section-header-row">
                  <div className="section-title-left">
                    <h3 className="section-title-label">Cases</h3>
                    <span className="section-count-pill">{casesList.length} cases</span>
                  </div>
                </div>

                {casesList.length === 0 ? (
                  <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0' }}>
                    <EmptyState title="No Cases Logged" subtitle="No active cases are currently registered for the distributor's songs." hasSearch={false} />
                  </div>
                ) : (
                  <>
                    <div className="cases-grid">
                      {pagedCases.map((item) => {
                        const isOpen = item.status === 1 || item.status === '1';
                        const statusClass = isOpen ? 'status-open' : 'status-closed';
                        return (
                          <div key={item.id || `${item.type}-${item.name || item.title}`} className="case-card">
                            <div className="case-card-top">
                              <div className="case-icon-wrapper">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                                  <polyline points="14 2 14 8 20 8" />
                                </svg>
                              </div>
                              <div className="case-info-details">
                                <h4 className="case-title">{item.name || item.title}</h4>
                                <span className="case-tags">Tags: {item.tags || item.description || 'Standard Case'}</span>
                              </div>
                            </div>

                            <div className="case-situation-box">
                              <span className="situation-title">Current Situation</span>
                              <div className={`situation-status-pill ${statusClass}`}>
                                <span className="status-dot"></span>
                                <span className="status-text">{isOpen ? 'Open' : 'Closed'}</span>
                              </div>
                            </div>

                            <div className="case-card-footer">
                              <button type="button" className="view-more-btn" onClick={() => onSelectCase && onSelectCase(item)}>
                                View More
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Pagination currentPage={casesPage} totalPages={Math.max(1, Math.ceil(casesList.length / 20))} totalCount={casesList.length} onPageChange={setCasesPage} />
                  </>
                )}
              </div>

              <hr className="sections-divider" />

              <div className="notes-section">
                <div className="section-header-row">
                  <div className="section-title-left">
                    <h3 className="section-title-label">Notes</h3>
                    <span className="section-count-pill">{notesList.length} notes</span>
                  </div>
                </div>

                {notesList.length === 0 ? (
                  <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0' }}>
                    <EmptyState title="No Notes Logged" subtitle="No notes are currently registered for the distributor's songs." hasSearch={false} />
                  </div>
                ) : (
                  <>
                    <div className="notes-grid">
                      {pagedNotes.map((note) => (
                        <div key={note.id || `${note.type}-${note.name || note.title}`} className="note-card">
                          <div className="note-card-body">
                            <div className="note-icon-wrapper">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </div>
                            <div className="note-info-details">
                              <h4 className="note-title">{note.name || note.title}</h4>
                              <span className="note-tags">Tags: {note.tags || note.description || 'Standard Note'}</span>
                            </div>
                          </div>

                          <div className="note-card-footer">
                            <button type="button" className="view-more-btn" onClick={() => onSelectCase && onSelectCase(note)}>
                              View More
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Pagination currentPage={notesPage} totalPages={Math.max(1, Math.ceil(notesList.length / 20))} totalCount={notesList.length} onPageChange={setNotesPage} />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Loading overlay for export */}
      <LoadingOverlay isLoading={isExportingSongs} message={t('common.exportingMessage') || 'Exporting data...'} />
    </div>
  );
}

export default DistributorDetails;
